import formatDate from "date-fns/format";
import sortBy from "lodash/sortBy";
import { useMemo } from "react";
import { Button, Container } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { groupBy } from "lodash";

import useCompetition, {
  useCompetitionRoutes,
  useCompetitionRoutesForJudge,
} from "../../../hooks/useCompetition";
import useLocalStorage from "../../../hooks/useLocalStorage";
import getCompetitionJudgingClosed from "../../../utils/getCompetitionJudgingClosed";
import getCompetitionRouteDisplay from "../../../utils/getCompetitionRouteDisplay";
import CategorySelector from "../../CategorySelector";
import { RouteCircle } from "../../RouteCircle";
import ScreenHeader from "../../ScreenHeader/ScreenHeader";
import * as Style from "./RouteSelectorScreen.style";
import { logout } from "../../../services/auth";
import { ApiCompetition, ApiRoute } from "../../../types";
import useCurrentUser from "../../../hooks/useCurrentUser";
import getCanSeeLeaderboard from "../../../utils/getCanSeeLeaderboard";

const useGroupedRoutesWithDisplay = ({
  routes,
  competition,
  selectedCategoryUUIDs,
}: {
  routes?: ApiRoute[];
  competition?: ApiCompetition;
  selectedCategoryUUIDs: string[];
}) => {
  const filteredRoutes = useMemo(() => {
    const routeGroups =
      (selectedCategoryUUIDs.length
        ? competition?.routeGroups.filter((x) =>
            x.categories.find((cat) => selectedCategoryUUIDs.includes(cat))
          )
        : competition?.routeGroups) || [];
    const routeGroupUUIDs = routeGroups.map((x) => x.uuid);
    return routes?.filter((route) =>
      route.competitionRouteGroups.find((rg) =>
        routeGroupUUIDs.includes(rg.uuid)
      )
    );
  }, [routes, selectedCategoryUUIDs, competition]);

  const groupedRoutesWithDisplay = useMemo(() => {
    const mappedRoutes = filteredRoutes?.map((route) => {
      return {
        route,
        display: getCompetitionRouteDisplay({
          route,
          categoryUUIDs: selectedCategoryUUIDs.length
            ? selectedCategoryUUIDs
            : undefined,
          routeGroups: route.competitionRouteGroups,
        }),
        judgingGroup: competition?.judgingGroups.find((jg) =>
          jg.routes.find((routeUUID) => routeUUID === route.uuid)
        ),
      };
    });
    const groupedRoutes = Object.values(
      groupBy(
        sortBy(mappedRoutes, [
          (mr) => mr.judgingGroup?.name || "",
          (mr) => mr.display.number,
        ]),
        (mr) => mr.judgingGroup?.uuid || "ungrouped"
      )
    );
    return groupedRoutes;
  }, [filteredRoutes, selectedCategoryUUIDs, competition?.judgingGroups]);

  return groupedRoutesWithDisplay;
};

export default function RouteSelector() {
  const { competitionUUID } = useParams();
  const navigate = useNavigate();
  const { competition } = useCompetition(competitionUUID);
  const judgeUUID = useCurrentUser().profile?.uuid;

  const { visibleRoutes: routes, isValidating: routesLoading } =
    useCompetitionRoutesForJudge({
      venueUUID: competition?.venues?.[0]?.uuid,
      competition,
      judgeUUID,
    });

  const [selectedCategoryUUIDs, setSelectedCategoryUUIDs] = useLocalStorage<
    string[]
  >(`${competitionUUID}/selectedCategories`, []);

  const selectedCategories = useMemo(
    () =>
      competition?.stages[0]?.categories.filter((cat) =>
        selectedCategoryUUIDs.includes(cat.uuid)
      ) || [],
    [selectedCategoryUUIDs, competition]
  );

  const isJudgingClosed =
    competition && getCompetitionJudgingClosed(competition);
  const competitionEndStr = useMemo(
    () =>
      competition &&
      formatDate(new Date(competition.endTime), "do MMMM yyyy 'at' HH:mm"),
    [competition]
  );

  const groupedRoutesWithDisplay = useGroupedRoutesWithDisplay({
    routes,
    competition,
    selectedCategoryUUIDs,
  });

  const canSeeLeaderboard = competition
    ? getCanSeeLeaderboard(competition)
    : undefined;

  return (
    <Container>
      {isJudgingClosed ? (
        <>
          <ScreenHeader canGoBack={false}>Competition closed</ScreenHeader>
          <p className="mt-3">
            This competition ended on {competitionEndStr}. Please contact the
            event organiser for any queries.
          </p>
        </>
      ) : (
        <>
          <ScreenHeader
            canGoBack={false}
            rightMenuItems={[
              ...(canSeeLeaderboard
                ? [
                    {
                      text: "Manage competitors",
                      onClick: () => navigate("../entries"),
                    },
                  ]
                : []),
            ]}
          >
            {competition?.name}
          </ScreenHeader>
          <Style.ContainerUpper>
            <CategorySelector
              categories={competition?.stages[0]?.categories || []}
              selectedCategories={selectedCategories}
              onChange={(categories) =>
                setSelectedCategoryUUIDs(categories.map((x) => x.uuid))
              }
            />
          </Style.ContainerUpper>
          {!routesLoading &&
          groupedRoutesWithDisplay.length === 0 &&
          selectedCategories.length === 0 ? (
            <div className="text-center">
              <p>No routes have been assigned to you yet. Check back later!</p>
              <small>
                (Think this is a mistake? Get in touch with the competition
                organiser.)
              </small>
            </div>
          ) : null}
          <Style.RouteListContainer>
            {groupedRoutesWithDisplay.map((routesWithDisplay) => (
              <>
                {routesWithDisplay[0]?.judgingGroup ? (
                  <Style.RouteListGroupHeader>
                    {routesWithDisplay[0].judgingGroup.name}
                  </Style.RouteListGroupHeader>
                ) : null}
                {routesWithDisplay.map(({ route, display }) => (
                  <Style.RouteListItem
                    key={route.uuid}
                    as={Link}
                    to={`judge/${route.uuid}`}
                    className="d-flex"
                  >
                    <RouteCircle
                      colors={(route.colors || "#FFFFFF").split(":")}
                      className="me-2"
                    />
                    {display.name.split("/").map((n, i) => (
                      <Style.NamePart key={i}>{n}</Style.NamePart>
                    ))}
                  </Style.RouteListItem>
                ))}
              </>
            ))}
          </Style.RouteListContainer>
          {competition?.settings.judgeCompetitorPermissions &&
          ["ADD", "ADD_MODIFY"].includes(
            competition?.settings.judgeCompetitorPermissions
          ) ? (
            <div className="text-center m-2">
              <Button
                href={`${competitionUUID}/enter/competitors`}
                target="_blank"
              >
                Add a competitor
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Container>
  );
}
